import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { catchError, debounceTime, distinctUntilChanged, map, NEVER, of, startWith, switchMap, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContactsService } from '../../services/contacts.service';
import { PartyService } from '../../../users/components/user-details/services/party.service';
import { Party } from '../../../users/components/user-details/models/user-details-form';
import { EditAssignment, EditContactDetails, UpdateContactEditRequest } from './models/edit-contact';
import { MIN_SEARCH_LENGTH } from '../add-person-contact/add-person-contact.component';
import { AvatarComponent } from '../../../../../../components/avatar/avatar.component';

export interface EditContactDialogData {
  id: number;
  // GET /supportContacts/{id} (getContactForEdit) only returns photoReference, an internal
  // sync token, not a renderable SAS URL like the search endpoint's Contact.photoUrl — so the
  // photo shown here is seeded straight off the grid row that opened this dialog, same as
  // ContactsComponent.editContact()'s Department branch seeds its own dialog from that row.
  photoUrl: string | null;
}

type AssignableParty = Party & { id: number };

interface PartySearchState {
  parties: AssignableParty[];
  loading: boolean;
  searched: boolean;
  error: boolean;
}

const EMPTY_PARTY_SEARCH_STATE: PartySearchState = { parties: [], loading: false, searched: false, error: false };

// Mirrors AddDepartmentContactComponent's own party picker: a hand-built, in-flow dropdown
// (not mat-autocomplete's floating overlay) with its own arrow/Enter/Escape handling, matching
// the design's dialog-wide "no Material form chrome" approach.
@Component({
  selector: 'app-edit-contact',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatProgressSpinner,
    AvatarComponent
  ],
  templateUrl: './edit-contact.component.html',
  styleUrl: './edit-contact.component.scss'
})
export class EditContactComponent implements OnInit {
  data = inject<EditContactDialogData>(MAT_DIALOG_DATA);

  contact: EditContactDetails | null = null;
  loading = true;
  hasError = false;
  saving = false;

  editForm = new FormGroup({
    showMobilePhone: new FormControl(false, { nonNullable: true }),
    showPhoto: new FormControl(false, { nonNullable: true }),
    assignments: new FormControl<EditAssignment[]>([], { nonNullable: true })
  });
  partyControl = new FormControl('', { nonNullable: true });
  partySearchState$ = of<PartySearchState>(EMPTY_PARTY_SEARCH_STATE);

  // Index of the row the arrow keys have moved to in the current party results; -1 is "nothing
  // highlighted". partyResults mirrors what the async pipe is currently rendering so
  // onPartyKeydown can act on the highlighted one — same pattern as AddDepartmentContactComponent.
  partyHighlight = -1;
  partyResults: AssignableParty[] = [];

  private contactsService = inject(ContactsService);
  private partyService = inject(PartyService);
  private matSnackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);
  private matDialogRef = inject(MatDialogRef<EditContactComponent>);

  ngOnInit() {
    this.loadContact();

    // Mirrors ContactDetailsComponent's party-autocomplete pipeline, gated to a minimum query
    // length so the search endpoint isn't hit on every keystroke (same MIN_SEARCH_LENGTH used
    // by AddPersonContactComponent's own client-side filter).
    this.partySearchState$ = this.partyControl.valueChanges.pipe(
      startWith(''),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query || query.trim().length < MIN_SEARCH_LENGTH) {
          return of<PartySearchState>(EMPTY_PARTY_SEARCH_STATE);
        }

        return this.partyService.getParties(query).pipe(
          map(parties => {
            const selectedPartyIds = new Set(this.editForm.controls.assignments.value.map(a => a.partyId));
            const withId = parties.filter((party): party is AssignableParty => party.id !== undefined);
            const uniqueParties = new Map(withId.map(party => [party.id, party]));

            return {
              parties: [...uniqueParties.values()].filter(party => !selectedPartyIds.has(party.id)),
              loading: false,
              searched: true,
              error: false
            };
          }),
          startWith<PartySearchState>({ parties: [], loading: true, searched: true, error: false }),
          catchError(() => of<PartySearchState>({ parties: [], loading: false, searched: true, error: true }))
        );
      }),
      // Keep the keydown handler's view of the list in step with what is on screen, and drop the
      // highlight whenever the results change underneath it.
      tap(state => {
        this.partyResults = state.parties;
        this.partyHighlight = -1;
        // The panel renders in flow, below a search box that can itself already sit near the
        // bottom of the dialog's own scrolled content — without this it renders out of view and
        // looks like nothing happened. Deferred a tick so the panel has actually been added to
        // the DOM by the time we scroll to it.
        if (state.loading || state.searched) {
          setTimeout(() => this.revealSearchPanel());
        }
      })
    );
  }

  onPartyKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.partyHighlight = this.moveHighlight(this.partyHighlight, this.partyResults.length, event.key);
      this.revealOption(this.partyHighlight);
      return;
    }
    if (event.key === 'Enter') {
      const party = this.partyResults[this.partyHighlight];
      if (party) {
        event.preventDefault();
        this.addAssignment(party);
      }
    }
  }

  remove({ partyId }: EditAssignment) {
    const assignmentsControl = this.editForm.controls.assignments;
    assignmentsControl.setValue(assignmentsControl.value.filter(assignment => assignment.partyId !== partyId));
  }

  clearAll() {
    this.editForm.controls.assignments.setValue([]);
  }

  addAssignment(party: AssignableParty) {
    const assignmentsControl = this.editForm.controls.assignments;
    const assignments = assignmentsControl.value;
    const alreadySelected = assignments.some(assignment => assignment.partyId === party.id);

    if (!alreadySelected) {
      // No manual reordering in this dialog - SortOrder just follows the order parties
      // were added.
      assignmentsControl.setValue([
        ...assignments,
        {
          partyId: party.id,
          partyNumber: party.number,
          partyName: party.name,
          role: null,
          sortOrder: assignments.length,
          showMobilePhone: null,
          showPhoto: null
        }
      ]);
    }

    this.partyControl.setValue('');
  }

  updateRole(partyId: number, role: string) {
    const assignmentsControl = this.editForm.controls.assignments;
    assignmentsControl.setValue(
      assignmentsControl.value.map(assignment =>
        assignment.partyId === partyId ? { ...assignment, role: role.trim() || null } : assignment
      )
    );
  }

  save() {
    if (!this.contact) {
      return;
    }

    const { showMobilePhone, showPhoto, assignments } = this.editForm.getRawValue();
    const request: UpdateContactEditRequest = {
      showMobilePhone,
      showPhoto,
      // Read-only in this dialog (follows the linked user) — echoed back unchanged; the
      // backend ignores it for a Person contact regardless of what is sent.
      isActive: this.contact.isActive,
      // showMobilePhone/showPhoto are per-organisation overrides edited from the Organisations
      // tab only — this dialog has no UI for them, so they're echoed back unchanged rather than
      // rebuilt, to avoid silently wiping an org's override on an unrelated Contacts-tab save.
      assignments: assignments.map((assignment, index) => ({
        partyId: assignment.partyId,
        role: assignment.role,
        sortOrder: index,
        showMobilePhone: assignment.showMobilePhone,
        showPhoto: assignment.showPhoto
      }))
    };

    this.saving = true;
    this.contactsService
      .updateContact(this.data.id, request)
      .pipe(
        tap(() => {
          this.matSnackBar.open('Contact updated', 'x', { panelClass: 'snackbar-green' });
          this.matDialogRef.close('Updated');
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.saving = false;
          this.matSnackBar.open('Could not update the contact. Please try again.', 'x');
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private loadContact() {
    this.loading = true;
    this.hasError = false;
    this.contactsService
      .getContactForEdit(this.data.id)
      .pipe(
        tap(contact => {
          this.contact = contact;
          this.editForm.patchValue({ showMobilePhone: contact.showMobilePhone, showPhoto: contact.showPhoto });
          this.editForm.controls.assignments.setValue(contact.assignments);
          this.loading = false;
        }),
        catchError(() => {
          this.hasError = true;
          this.loading = false;
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Wraps at both ends, and leaves the highlight cleared when there is nothing to move over.
  // Same as AddDepartmentContactComponent.moveHighlight().
  private moveHighlight(current: number, count: number, key: string): number {
    if (count === 0) {
      return -1;
    }
    const step = key === 'ArrowDown' ? 1 : -1;
    return (current + step + count) % count;
  }

  // Keeps the arrow-key highlight inside the panel's scroll window. Same as
  // AddDepartmentContactComponent.revealOption().
  private revealOption(index: number) {
    if (index < 0) {
      return;
    }
    document.getElementById(`edit-contact-party-opt-${index}`)?.scrollIntoView({ block: 'nearest' });
  }

  private revealSearchPanel() {
    document.getElementById('edit-contact-party-list')?.scrollIntoView({ block: 'nearest' });
  }
}
